import React, { useState, useRef, useEffect } from "react";
import { Box, Typography, Button } from "@mui/material";
import { motion, AnimatePresence, Variants } from "framer-motion";
import useScrollToSection from "../../hooks/useScrollToSection";
import { useTranslation } from "react-i18next";
import DotGrid from "../ui/DotGrid";

const BLOCK: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1 } },
};

const ITEM: Variants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 1.2, ease: [0.16, 0.77, 0.47, 0.97] },
  },
};

const viewport = { once: true, amount: 0.1 };

const CONTENT_VARIANTS: Variants = {
  hidden: { opacity: 0, y: 12 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: [0.16, 0.77, 0.47, 0.97] },
  },
  exit: { opacity: 0, y: -8, transition: { duration: 0.25 } },
};

const Capabilities: React.FC = () => {
  const { t } = useTranslation();
  const keyCapabilities = t("capabilities.keyCapabilities");
  const ITEMS = (
    t("capabilities.items", { returnObjects: true }) as Array<{
      label: string;
      title: string;
      description: string;
      capabilities: string[];
      cta: string;
    }>
  ).map((item, id) => ({ ...item, id }));

  const [active, setActive] = useState(0);
  const selected = ITEMS[active];
  const scrollToSection = useScrollToSection();
  const impulseRef = useRef<number | null>(null);

  useEffect(() => {
    const schema = {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: "Kanonics Services",
      itemListElement: ITEMS.map((item, index) => ({
        "@type": "ListItem",
        position: index + 1,
        item: {
          "@type": "Service",
          name: item.title,
          description: item.description,
          provider: {
            "@type": "Organization",
            name: "Kanonics",
            url: "https://www.kanonics.com",
          },
          areaServed: "Worldwide",
          hasOfferCatalog: {
            "@type": "OfferCatalog",
            name: item.title,
            itemListElement: item.capabilities.map((cap) => ({
              "@type": "Offer",
              itemOffered: { "@type": "Service", name: cap },
            })),
          },
        },
      })),
    };
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "services-schema";
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);
    return () => {
      document.getElementById("services-schema")?.remove();
    };
  }, [ITEMS]);

  const handleItemClick = (id: number) => {
    setActive(id);
    impulseRef.current = performance.now();
  };

  useEffect(() => {
    const handler = (e: Event) => {
      const idx = (e as CustomEvent<number>).detail;
      setActive(idx);
      impulseRef.current = performance.now();
    };
    window.addEventListener("capability:open", handler);
    return () => window.removeEventListener("capability:open", handler);
  }, []);

  return (
    <Box
      component="section"
      id="solutions"
      sx={{
        bgcolor: "#f8f9fb",
        position: "relative",
        overflow: "hidden",
        isolation: "isolate",
      }}
    >
      {/* Top border - left segment */}
      <Box
        sx={{
          display: { xs: "none", md: "block" },
          position: "absolute",
          top: 0,
          left: 0,
          width: "calc(max(0px,(100% - 1280px)/2) + 462px)",
          height: "1px",
          bgcolor: "rgba(29, 47, 65, 0.15)",
        }}
      />
      {/* Top border - right segment */}
      <Box
        sx={{
          display: { xs: "none", md: "block" },
          position: "absolute",
          top: 0,
          left: "calc(max(0px,(100% - 1280px)/2) + 562px)",
          right: 0,
          height: "1px",
          bgcolor: "rgba(29, 47, 65, 0.15)",
        }}
      />
      {/* Top border - mobile */}
      <Box
        sx={{
          display: { xs: "block", md: "none" },
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "1px",
          bgcolor: "rgba(29, 47, 65, 0.15)",
        }}
      />

      {/* Bottom border - left segment */}
      <Box
        sx={{
          display: { xs: "none", md: "block" },
          position: "absolute",
          bottom: 0,
          left: 0,
          width: "calc(max(0px,(100% - 1280px)/2) + 462px)",
          height: "1px",
          bgcolor: "rgba(29, 47, 65, 0.15)",
        }}
      />
      {/* Bottom border - right segment */}
      <Box
        sx={{
          display: { xs: "none", md: "block" },
          position: "absolute",
          bottom: 0,
          left: "calc(max(0px,(100% - 1280px)/2) + 562px)",
          right: 0,
          height: "1px",
          bgcolor: "rgba(29, 47, 65, 0.15)",
        }}
      />
      {/* Bottom border - mobile */}
      <Box
        sx={{
          display: { xs: "block", md: "none" },
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          height: "1px",
          bgcolor: "rgba(29, 47, 65, 0.15)",
        }}
      />

      {/* Vertical column divider */}
      <Box
        sx={{
          display: { xs: "none", md: "block" },
          position: "absolute",
          top: 0,
          bottom: 0,
          left: "calc(max(0px,(100% - 1280px)/2) + 512px)",
          width: "1px",
          bgcolor: "rgba(29, 47, 65, 0.15)",
        }}
      />

      <Box
        sx={{
          maxWidth: 1280,
          mx: "auto",
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          height: { md: "680px" },
        }}
      >
        {/* Left - item list */}
        <Box
          sx={{
            width: { md: "40%" },
            flexShrink: 0,
            px: { xs: 3, md: 6 },
            py: { xs: 6, md: 10 },
          }}
        >
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={viewport}
            variants={BLOCK}
          >
            {ITEMS.map((item) => (
              <motion.div
                key={item.id}
                variants={ITEM}
                style={{ willChange: "transform" }}
              >
                <Box
                  onClick={() => handleItemClick(item.id)}
                  sx={{
                    position: "relative",
                    py: 2.5,
                    width: { md: "414px" },
                    cursor: "pointer",
                    transition: "all 0.2s",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "0.7rem",
                      fontWeight: 600,
                      letterSpacing: "0.1em",
                      textTransform: "uppercase",
                      fontFamily: '"Calibri", sans-serif',
                      color:
                        active === item.id
                          ? "#1D2F41"
                          : "rgba(29, 47, 65, 0.4)",
                      transition: "color 0.2s",
                    }}
                  >
                    {item.label}
                  </Typography>
                  {/* Desktop - stops 50px before vertical line */}
                  <Box
                    sx={{
                      display: { xs: "none", md: "block" },
                      position: "absolute",
                      bottom: 0,
                      left: 0,
                      width: "414px",
                      height: "1px",
                      bgcolor: "rgba(29, 47, 65, 0.1)",
                    }}
                  />
                  {/* Mobile - full width */}
                  <Box
                    sx={{
                      display: { xs: "block", md: "none" },
                      position: "absolute",
                      bottom: 0,
                      left: 0,
                      right: 0,
                      height: "1px",
                      bgcolor: "rgba(29, 47, 65, 0.1)",
                    }}
                  />
                </Box>
              </motion.div>
            ))}
          </motion.div>
        </Box>

        {/* Right - content */}
        <Box
          sx={{
            flex: 1,
            px: { xs: 3, md: 8 },
            py: { xs: 6, md: 10 },
            display: "flex",
            alignItems: "flex-start",
            overflow: "hidden",
            position: "relative",
          }}
        >
          <Box sx={{ position: "relative", zIndex: 1, width: "100%" }}>
            <AnimatePresence mode="wait">
              <motion.div
                key={active}
                variants={CONTENT_VARIANTS}
                initial="hidden"
                animate="visible"
                exit="exit"
                style={{ width: "100%" }}
              >
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { xs: "2rem", md: "2.8rem" },
                    fontWeight: 700,
                    letterSpacing: "-0.025em",
                    lineHeight: 1.2,
                    color: "#1D2F41",
                    fontFamily: '"Segoe UI Semibold", "Segoe UI", sans-serif',
                    mb: 3,
                  }}
                >
                  {selected.title}
                </Typography>
                <Typography
                  sx={{
                    fontSize: { xs: "1rem", md: "1.05rem" },
                    fontWeight: 300,
                    lineHeight: 1.75,
                    color: "rgba(29, 47, 65, 0.7)",
                    fontFamily: '"Calibri", sans-serif',
                    mb: 3,
                    maxWidth: "520px",
                  }}
                >
                  {selected.description}
                </Typography>

                <Typography
                  sx={{
                    fontSize: "0.7rem",
                    fontWeight: 600,
                    letterSpacing: "0.1em",
                    textTransform: "uppercase",
                    fontFamily: '"Calibri", sans-serif',
                    color: "rgba(29, 47, 65, 0.45)",
                    mb: 1.5,
                  }}
                >
                  {keyCapabilities}
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 5 }}>
                  {selected.capabilities.map((cap) => (
                    <Typography
                      key={cap}
                      sx={{
                        fontSize: "0.8rem",
                        fontWeight: 400,
                        fontFamily: '"Calibri", sans-serif',
                        color: "rgba(29, 47, 65, 0.7)",
                        bgcolor: "rgba(29, 47, 65, 0.07)",
                        px: 1.5,
                        py: 0.5,
                      }}
                    >
                      {cap}
                    </Typography>
                  ))}
                </Box>

                <Button
                  variant="contained"
                  disableElevation
                  endIcon={<span>→</span>}
                  onClick={() => scrollToSection("contact")}
                  sx={{
                    bgcolor: "#2b616121",
                    color: "#1D2F41",
                    px: 3,
                    py: 1.5,
                    borderRadius: 0,
                    fontSize: "0.95rem",
                    fontWeight: 500,
                    textTransform: "none",
                    fontFamily: '"Calibri", sans-serif',
                    "&:hover": { bgcolor: "rgba(43, 97, 97, 0.2)" },
                  }}
                >
                  {selected.cta}
                </Button>
              </motion.div>
            </AnimatePresence>
          </Box>
        </Box>
      </Box>

      {/* Decorative dot grid - right viewport edge */}
      <Box
        sx={{
          display: { xs: "none", md: "block" },
          position: "absolute",
          right: 0,
          top: "50%",
          transform: "translate(50%, -50%)",
          pointerEvents: "none",
          zIndex: -1,
        }}
      >
        <DotGrid impulseRef={impulseRef} />
      </Box>
    </Box>
  );
};

export default Capabilities;
